import type { IntlShape } from "@formatjs/intl";
import { Button, Flex, Tooltip } from "antd";
import type { EmojiEnum } from "../../app/generated-axios-client/myModel/emoji-enum";
import {
  COMMENT_REACTION_EMOJIS,
  COMMENT_REACTION_GLYPHS,
} from "../constants/commentReactions";
import styles from "./CommentReactions.module.css";

export type CommentReactionUser = {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
};

export type CommentReaction = {
  id?: string;
  emoji: EmojiEnum;
  user: CommentReactionUser;
};

type CommentReactionsProps = {
  reactions: CommentReaction[] | undefined;
  currentUserId: number | undefined;
  pendingEmojis: EmojiEnum[];
  onToggle: (emoji: EmojiEnum) => void;
  getUserName: (user: CommentReactionUser) => string;
  intl: IntlShape;
  alignEnd?: boolean;
};

export const CommentReactions = ({
  reactions,
  currentUserId,
  pendingEmojis,
  onToggle,
  getUserName,
  intl,
  alignEnd = false,
}: CommentReactionsProps) => {
  const reactionGroups = COMMENT_REACTION_EMOJIS.map((emoji) => ({
    emoji,
    emojiReactions: (reactions ?? []).filter((reaction) => reaction.emoji === emoji),
  })).filter(({ emojiReactions }) => emojiReactions.length > 0);

  if (reactionGroups.length === 0) return null;

  return (
    <Flex
      wrap
      gap={4}
      justify={alignEnd ? "flex-end" : "flex-start"}
      className={styles.reactionList}
    >
      {reactionGroups.map(({ emoji, emojiReactions }) => {
        const reactedByCurrentUser = emojiReactions.some(
          (reaction) => reaction.user.id === currentUserId
        );
        const reactorNames = emojiReactions
          .map((reaction) =>
            reaction.user.id === currentUserId
              ? intl.formatMessage({ id: "comment_reaction_you" })
              : getUserName(reaction.user)
          )
          .join(", ");

        return (
          <Tooltip key={emoji} title={reactorNames}>
            <Button
              size="small"
              className={
                reactedByCurrentUser
                  ? `${styles.reactionChip} ${styles.reactionChipMine}`
                  : styles.reactionChip
              }
              aria-pressed={reactedByCurrentUser}
              aria-label={intl.formatMessage(
                { id: "comment_reaction_chip_label" },
                {
                  reaction: intl.formatMessage({ id: `comment_reaction_${emoji}` }),
                  count: emojiReactions.length,
                  names: reactorNames,
                }
              )}
              disabled={pendingEmojis.includes(emoji)}
              onClick={() => onToggle(emoji)}
            >
              {COMMENT_REACTION_GLYPHS[emoji]} {emojiReactions.length}
            </Button>
          </Tooltip>
        );
      })}
    </Flex>
  );
};
